import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useTenants } from './hooks/useTenants'
import { TenantCard } from './TenantCard'
import { CreateTenantDialog } from './CreateTenantDialog'
import { EditTenantDialog } from './EditTenantDialog'
import { DeleteTenantDialog } from './DeleteTenantDialog'
import type { Tenant } from '@/types/auth'

export function AdminTenantsPage() {
  const { t } = useTranslation()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null)
  const [deletingTenant, setDeletingTenant] = useState<Tenant | null>(null)
  const { data: tenants, isLoading, error } = useTenants()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('admin.tenants.title')}</h1>
          <p className="text-muted-foreground">
            {t('admin.tenants.subtitle')}
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>{t('admin.tenants.newTenant')}</Button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">{t('common.loading')}</div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-destructive">
            {t('common.error')}: {error.message}
          </p>
        </div>
      )}

      {!isLoading && !error && tenants?.length === 0 && (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <h3 className="text-lg font-medium">{t('admin.tenants.noTenants')}</h3>
          <p className="text-muted-foreground mt-1">
            {t('admin.tenants.noTenantsMessage')}
          </p>
          <Button className="mt-4" onClick={() => setIsCreateDialogOpen(true)}>
            {t('admin.tenants.newTenant')}
          </Button>
        </div>
      )}

      {!isLoading && !error && tenants && tenants.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tenants.map((tenant) => (
            <TenantCard
              key={tenant.id}
              tenant={tenant}
              onEdit={setEditingTenant}
              onDelete={setDeletingTenant}
            />
          ))}
        </div>
      )}

      <CreateTenantDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      <EditTenantDialog
        tenant={editingTenant}
        open={editingTenant !== null}
        onOpenChange={(open) => {
          if (!open) setEditingTenant(null)
        }}
      />

      <DeleteTenantDialog
        tenant={deletingTenant}
        open={deletingTenant !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingTenant(null)
        }}
      />
    </div>
  )
}
